import { IDomRect } from "../../interfaces/Misc";

export default class DomUtils
{
	/**
	 * checks whether the given element exists in the hierarchy of the given root
	 * @static
	 * @param {HTMLElement} element the element from which we start the test
	 * @param {HTMLElement} root the potential ancestor we're looking for
	 * @returns true if, after going up the element's hierarchy we encountered the root, or false otherwise
	 * @memberof DomUtils
	 */
	/* eslint-disable no-cond-assign */
	static isDomElementDescendantOfRoot(element: HTMLElement, root: HTMLElement)
	{
		let el = element.parentElement as HTMLElement;
		while (el)
		{
			if (el === root)
			{
				return true;
			}

			el = el.parentElement as HTMLElement;
		}

		return false;
	}

	/* eslint-enable no-cond-assign */
	/**
	 * calculates the position and dimensions of the given HTML element in a rect format
	 *
	 * uses DomUtils.getDomElementPosition internally
	 * @static
	 * @param {HTMLElement} element
	 * @returns a *read-only* data object containing the position and dimensions of the element, or undefined if the element is null or undefined
	 * @memberof DomUtils
	*/
	static getDomElementRect(element: HTMLElement): IDomRect | undefined
	{
		if (!element) return;

		const elementPosition = DomUtils.getDomElementPosition(element);
		return Object.freeze({
			xMin: elementPosition.x,
			yMin: elementPosition.y,
			xMax: elementPosition.x + element.offsetWidth,
			yMax: elementPosition.y + element.offsetHeight,
			width: element.offsetWidth,
			height: element.offsetHeight,
			left: elementPosition.x,
			top: elementPosition.y,
			right: elementPosition.x + element.offsetWidth,
			bottom: elementPosition.y + element.offsetHeight,
		});
	}

	/**
	 * calculates the real position of the given HTML element, taking into account parent offsets and scroll values
	 * - taken from: https://www.kirupa.com/html5/get_element_position_using_javascript.htm
	 * @static
	 * @param {HTMLElement} element the element to find the position of
	 * @returns an {x: number, y: number} object containing the element's position in pixels relative to the page's body element
	 * @memberof DomUtils
	*/
	static getDomElementPosition(element: HTMLElement)
	{
		let xPos = 0;
		let yPos = 0;

		let el = element;
		while (el)
		{
			if (el.tagName === "BODY")
			{
				// deal with browser quirks with body/window/document and page scroll
				const xScroll = el.scrollLeft || document.documentElement.scrollLeft;
				const yScroll = el.scrollTop || document.documentElement.scrollTop;

				xPos += (el.offsetLeft - xScroll + el.clientLeft);
				yPos += (el.offsetTop - yScroll + el.clientTop);
			}
			else
			{
				// for all other non-BODY elements
				xPos += (el.offsetLeft - el.scrollLeft + el.clientLeft);
				yPos += (el.offsetTop - el.scrollTop + el.clientTop);
			}

			el = el.offsetParent as HTMLElement;
		}
		return {
			x: xPos,
			y: yPos
		};
	}

	/**
	 * calculates the total amount of scroll offsets of the given HTML element, going recursively up the element's hierarchy
	 * @static
	 * @param {HTMLElement} element the element for which we want to calculate total scroll offsets
	 * @returns an {x: number, y: number} object containing the total amount of scrollLeft and scrollTop, respectively, of the element and its upward hierarchy up to the page's body element
	 * @memberof DomUtils
	 */
	static getScrollOffsets(element: HTMLElement)
	{
		let xScroll = 0;
		let yScroll = 0;

		let el = element;
		while (el)
		{
			if (el.tagName === "BODY")
			{
				// deal with browser quirks with body/window/document and page scroll
				xScroll += el.scrollLeft || document.documentElement.scrollLeft;
				yScroll += el.scrollTop || document.documentElement.scrollTop;
			}
			else
			{
				// for all other non-BODY elements
				xScroll += el.scrollLeft;
				yScroll += el.scrollTop;
			}

			el = el.offsetParent as HTMLElement;
		}
		return {
			x: xScroll,
			y: yScroll
		};
	}
	
	/**
	 * searches the given element for the given attributeName, going recursively up the hierarchy
	 * @static
	 * @param {HTMLElement} element an HTML element that serves as the starting point for the recursive search
	 * @param {String} attributeName the name of the attribute to look for
	 * @returns if the attribute was found on an element, the element will be returned; otherwise, the function will return 'undefined'
	 * @memberof DomUtils
	 */
	static getAttributeFromElementRecursive(element: HTMLElement, attributeName: string)
	{
		let el = element;
		while (el)
		{
			if (el.hasAttribute && el.hasAttribute(attributeName))
			{
				return el.getAttribute(attributeName);
			}

			el = el.parentElement as HTMLElement;
		}

		return undefined;
	}

	
	/**
	 * get the tag name of the given html string, for example: <div>hello</div> will return 'div'
	 * @static
	 * @param {string} htmlString
	 * @returns the tag name of the given html string or an empty string if no tag name was found
	 * @memberof DomUtils
	 */
	static getTagNameFromString(htmlString: string)
	{
		const tagNameRegex = /<\/?([a-zA-Z0-9]+)[^>]*>/;
		const tagNameMatch = htmlString.match(tagNameRegex);
		if (tagNameMatch && tagNameMatch.length > 1)
		{
			return tagNameMatch[1];
		}
		
		return "";
	}

	/**
	 * just maps some common attribute names to their React equivalents
	 * e.g. class -> className, onclick -> onClick
	 * @static
	 * @param {string} attributeName
	 * @returns {string}
	 * @memberof DomUtils
	 */
	static attributeNameToReactName(attributeName: string): string
	{
		const attributeMap = {
			"class": "className",
			"onclick": "onClick",
		};

		return attributeMap[attributeName] || attributeName;
	}
}